/**
 * Blocs E-M : Risques majeurs ADT Lafarge
 * Chaque bloc contient des cases à cocher. Si ≥ 1 cochée → le bloc OUI s'active
 * et le permis correspondant est suggéré au demandeur.
 */
import { AlertTriangle, Flame, Wrench, ArrowUp, Box, Users, Zap, Clock, ArrowLeftRight, CheckCircle2, Waves, Shovel } from 'lucide-react';

const BLOCKS = [
  {
    id: 'E',
    title: 'Énergies dangereuses',
    icon: Zap,
    accent: 'border-red-300 bg-red-50',
    header: 'bg-red-700 text-white',
    items: [
      'Arrivée matière (gaz/liquide/solide) gravitaire ou sous pression',
      'Électrique',
      'Hydraulique (eau/huile/graisse/fluide thermique...)',
      'Ionisante (densimètre/niveau/débit...)',
      'Matière (vidange/accrochage...)',
      'Mécanique',
      'Pneumatique',
      'Potentielle (contrepoids/ressort/condo/batterie/dévirage/inertie...)',
      'Thermique (zone chaude MSR, gaz/liquide/solide chauds...)',
    ],
    triggers: ['bon_isolation_energies', 'attestation_consignation_elec'],
    triggerLabel: "Bon d'isolation énergétique (LOTOTO) — toujours requis ; Attestation de consignation électrique requise si sous-traitant",
  },
  {
    id: 'F',
    title: 'Travail par points chauds',
    icon: Flame,
    accent: 'border-orange-300 bg-orange-50',
    header: 'bg-orange-600 text-white',
    items: [
      'Stockage, transport, injection combustibles liquides ?',
      'Stockage, transport, injection Coke ou Charbon ?',
      'Stockage, transport, injection AFR ?',
      'Autres ATEX, sacheries, tapis caoutchouc, stock graisse... ?',
    ],
    triggers: ['permis_feu'],
    triggerLabel: 'Permis de feu obligatoire si une des 4 cases cochée',
  },
  {
    id: 'G',
    title: 'Levage et manutention',
    icon: Wrench,
    accent: 'border-sky-300 bg-sky-50',
    header: 'bg-sky-700 text-white',
    items: [
      'Chariot manuscopique en levage > 3m',
      'Levage avec grue > 3m',
      'Levage avec palan électrique > 3m',
    ],
    triggers: ['permis_levage'],
    triggerLabel: 'Permis de levage complexe obligatoire',
  },
  {
    id: 'H',
    title: 'Travail en hauteur',
    icon: ArrowUp,
    accent: 'border-blue-300 bg-blue-50',
    header: 'bg-blue-700 text-white',
    items: [
      'Utilisation de la nacelle',
      'Travail avec risque de chute de hauteur',
      'Retenue (longe sans absorbeur)',
    ],
    triggers: ['permis_hauteur', 'permis_nacelle'],
    triggerLabel: 'Permis travail en hauteur / Permis nacelle',
  },
  {
    id: 'I',
    title: 'Espace confiné',
    icon: Box,
    accent: 'border-emerald-300 bg-emerald-50',
    header: 'bg-emerald-700 text-white',
    items: [
      'Travail par points chauds en espace confiné',
      'Espace confiné non déclassé',
    ],
    triggers: ['permis_espace_confine'],
    triggerLabel: "Permis d'entrer en espace confiné obligatoire",
  },
  {
    id: 'J',
    title: 'Coactivité / interférence',
    icon: Users,
    accent: 'border-violet-300 bg-violet-50',
    header: 'bg-violet-700 text-white',
    items: [
      'Présence de coactivité / interférence',
      'Présence de circulation engins / VL / PL / piétons',
    ],
    triggers: [],
    triggerLabel: 'Séparation des flux, balisage, arbitrage N+1',
  },
  {
    id: 'K',
    title: "Travaux d'ordre électriques ou dépannage",
    icon: Zap,
    accent: 'border-rose-300 bg-rose-50',
    header: 'bg-rose-700 text-white',
    items: [
      "Risque d'électrocution / électrisation - pièces nues sous tension ?",
      'Étape 1, 2 et 3 : Identifications, séparation et condamnation',
      "Étape 4 : Vérification d'absence de tension (VAT)",
      'Étape 5 : Mise à la terre (MALT et/ou CC)',
      'Présence de condensateurs ou sources de secours ?',
    ],
    triggers: ['attestation_consignation_elec'],
    triggerLabel: 'Attestation de consignation électrique requise',
  },
  {
    id: 'L',
    title: "Travaux imprévus ou d'astreinte",
    icon: Clock,
    accent: 'border-indigo-300 bg-indigo-50',
    header: 'bg-indigo-700 text-white',
    items: [
      "Risque de démarrage de l'équipement ? / si oui, bon de consignation",
      'Risque de poussière, bruit, température ? / cocher les EPI à porter',
      'Risque de projection de matière ? / Isoler la source',
      'Risque de collision ? / Baliser votre zone de travail',
      'Risques liés aux produits chimiques ? / consulter FDS',
      'Risque de chute de matériaux ? / sécuriser ou évacuer',
      'Risques majeurs ? / utiliser les blocs prévus à cet effet',
    ],
    triggers: [],
    triggerLabel: 'Mesures de prévention spécifiques',
  },
  {
    id: 'M',
    title: 'Retours chantier vers préparations',
    icon: ArrowLeftRight,
    accent: 'border-green-300 bg-green-50',
    header: 'bg-green-700 text-white',
    items: [
      'Améliorations à reporter en préparation',
      'Documents complémentaires nécessaires',
      'Retour expérience à partager',
    ],
    triggers: [],
    triggerLabel: 'Boucle d\'amélioration continue',
  },
  {
    id: 'N',
    title: "Travail en bordure d'eau",
    icon: Waves,
    accent: 'border-cyan-300 bg-cyan-50',
    header: 'bg-cyan-700 text-white',
    items: [
      "Travail à proximité d'un point d'eau (risque de noyade)",
    ],
    triggers: ['permis_bordure_eau'],
    triggerLabel: "Permis travail en bordure d'eau obligatoire",
  },
  {
    id: 'O',
    title: 'Travail de fouille / terrassement',
    icon: Shovel,
    accent: 'border-amber-300 bg-amber-50',
    header: 'bg-amber-700 text-white',
    items: [
      'Travaux de terrassement, excavation, tranchée',
    ],
    triggers: ['permis_fouille'],
    triggerLabel: 'Permis de fouille obligatoire',
  },
];

export const RISK_BLOCKS = BLOCKS;

export default function RisksBlocks({ value, onChange }) {
  // value = { E: [bool, ...], F: [...], ... }
  const get = (id) => value?.[id] || [];
  const set = (id, idx, checked) => {
    const next = { ...(value || {}) };
    const arr = [...(next[id] || [])];
    arr[idx] = checked;
    next[id] = arr;
    onChange(next);
  };
  const anyChecked = (id) => get(id).some(Boolean);

  return (
    <div className="grid md:grid-cols-3 gap-3">
      {BLOCKS.map((b) => {
        const triggered = anyChecked(b.id);
        return (
          <div
            key={b.id}
            data-testid={`risk-block-${b.id}`}
            className={`border-2 ${triggered ? 'border-[#09090B]' : 'border-stone-200'} transition-all`}
          >
            <div className={`${b.header} px-3 py-2 flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <b.icon className="w-4 h-4" strokeWidth={2.5} />
                <span className="text-[11px] font-bold uppercase tracking-wider">
                  {b.id} — {b.title}
                </span>
              </div>
              <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 ${triggered ? 'bg-white text-[#09090B]' : 'bg-black/20 text-white/80'}`}>
                {triggered ? 'OUI ✓' : 'NON'}
              </span>
            </div>
            <div className={`p-3 ${b.accent} space-y-2`}>
              {b.items.map((it, i) => (
                <label key={i} className="flex items-start gap-2 cursor-pointer text-xs leading-snug">
                  <input
                    type="checkbox"
                    data-testid={`risk-${b.id}-${i}`}
                    checked={!!get(b.id)[i]}
                    onChange={(e) => set(b.id, i, e.target.checked)}
                    className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[#DA291C]"
                  />
                  <span className="text-stone-800 break-words">{it}</span>
                </label>
              ))}
              {triggered && b.triggers.length > 0 && (
                <div className="mt-3 pt-2 border-t border-stone-300 flex items-start gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#DA291C] flex-shrink-0 mt-0.5" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#DA291C] leading-snug">
                    {b.triggerLabel}
                  </span>
                </div>
              )}
              {triggered && b.triggers.length === 0 && (
                <div className="mt-3 pt-2 border-t border-stone-300 text-[10px] text-stone-600 italic">
                  ⚠ {b.triggerLabel}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
